import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Button, BackButton } from '../components/common';
import { navigateBack } from '../utils/navigationUtils';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { runSessionSimulation } from '../utils/sessionSimulationEngine';
import {
  transformSimulationToGamesData,
  transformSimulationToInteractionsData,
  transformSimulationToSummaryData,
} from '../utils/simulationDataTransformers';
import SessionReviewGamesTable from '../components/session/SessionReviewGamesTable';
import SessionReviewInteractionsTable from '../components/session/SessionReviewInteractionsTable';
import SessionReviewPlayerSummaryTable from '../components/session/SessionReviewPlayerSummaryTable';
import PlayerCategoriesTable from '../components/session/PlayerCategoriesTable';
import SimulationSummary from '../components/session/SimulationSummary';
import { getGenderTextColor } from '../utils/genderColors';
import PlayerSearchInput from '../components/common/PlayerSearchInput';
import { SCORING_PRESETS } from '@badminton/matchmaking-core/pure/scoringPresets.js';

const SessionSimulation = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configuration state
  const [clubPlayers, setClubPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [numberOfCourts, setNumberOfCourts] = useState(4);
  const [partnerships, setPartnerships] = useState([]);
  const [targetGames, setTargetGames] = useState({});
  const [defaultTargetGames, setDefaultTargetGames] = useState(6);
  const [gameTypeRestriction, setGameTypeRestriction] = useState('balanced_only');
  const [playerCategorization, setPlayerCategorization] = useState(true);
  const [randomPlayerCount, setRandomPlayerCount] = useState(30);

  // Scoring system selection
  const [scoringSystem, setScoringSystem] = useState('normalized'); // 'legacy' or 'normalized'
  const [scoringPreset, setScoringPreset] = useState('default'); // For normalized scoring
  const [varietyAwarePoolSelection, setVarietyAwarePoolSelection] = useState(false);
  const [scoringFirstMatchmaking, setScoringFirstMatchmaking] = useState(false);

  // Legacy matchmaking parameters
  const [matchmakingParams, setMatchmakingParams] = useState({
    enableCooldown: true,
    waitGuaranteeThreshold: 4,
    enableInteractionPenalties: true,
    sameTeamLowThreshold: 20,
    sameTeamHighThreshold: 25,
    totalLowThreshold: 20,
    totalHighThreshold: 50,
    interactionPenaltyWeight: 0.4,
    eloBalanceMultiplier: 30,
    categoryGapHardBlock: 3, // Hard block games with category gap >= this value (1, 2, 3, or 4+)
    enableConsecutiveInteractionBlock: true, // Enable/disable consecutive interaction hard block
    consecutiveInteractionThreshold: 2, // Block on Nth consecutive interaction (2 = block 3rd consecutive)
  });

  // Normalized scoring parameters (dimension weights)
  const [normalizedWeights, setNormalizedWeights] = useState({
    fairness: 0.35,      // 35% - Games played balance
    eloBalance: 0.25,    // 25% - Competitive balance
    interactions: 0.20,  // 20% - Variety/freshness
    gameType: 0.10,      // 10% - Balanced game types
    categories: 0.10,    // 10% - Skill tier balance
  });

  // Simulation state
  const [isRunning, setIsRunning] = useState(false);
  const [simulationResults, setSimulationResults] = useState(null);
  const [activeTab, setActiveTab] = useState('settings'); // Start with settings tab
  const [selectedFilterPlayer, setSelectedFilterPlayer] = useState(null);

  // Get navigation context
  const fromMyClubs = location.state?.fromMyClubs;
  const fromHomepage = location.state?.fromHomepage;
  const fromManageClubs = location.state?.fromManageClubs;

  useEffect(() => {
    if (clubId) {
      fetchClubDetails();
      fetchClubPlayers();
    }
  }, [clubId]);

  const fetchClubDetails = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (error) throw error;
      setClub(data);
    } catch (err) {
      console.error('Error fetching club details:', err);
      setError('Failed to load club details');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubPlayers = async () => {
    try {
      // Fetch players from the club
      const { data: playersData, error: playersError } = await supabase
        .from('players_club')
        .select(`
          player_id,
          players!inner(
            id,
            first_name,
            last_name,
            gender
          )
        `)
        .eq('club_id', clubId)
        .eq('is_active', true);

      if (playersError) throw playersError;

      // Get player IDs for ELO query
      const playerIds = playersData.map(p => p.player_id);

      // Fetch ELO ratings
      const { data: eloData, error: eloError } = await supabase
        .from('player_club_elo_ratings')
        .select('player_id, mixed_elo_rating, levels_elo_rating, mixed_games_played, levels_games_played')
        .eq('club_id', clubId)
        .in('player_id', playerIds);

      if (eloError) {
        console.warn('Error fetching ELO ratings:', eloError);
      }

      // Create ELO map
      const eloMap = {};
      (eloData || []).forEach(elo => {
        eloMap[elo.player_id] = {
          mixed_elo_rating: elo.mixed_elo_rating || 1200,
          levels_elo_rating: elo.levels_elo_rating || 1200,
          mixed_games_played: elo.mixed_games_played || 0,
          levels_games_played: elo.levels_games_played || 0,
        };
      });

      // Combine player data with ELO
      const players = playersData.map(p => ({
        id: p.player_id,
        first_name: p.players?.first_name || '',
        last_name: p.players?.last_name || '',
        gender: p.players?.gender || 'unknown',
        mixed_elo_rating: eloMap[p.player_id]?.mixed_elo_rating || 1200,
        levels_elo_rating: eloMap[p.player_id]?.levels_elo_rating || 1200,
        mixed_games_played: eloMap[p.player_id]?.mixed_games_played || 0,
        levels_games_played: eloMap[p.player_id]?.levels_games_played || 0,
      }));

      setClubPlayers(players);
    } catch (err) {
      console.error('Error fetching club players:', err);
      setError('Failed to load club players');
    }
  };

  const handleSelectRandomPlayers = () => {
    if (randomPlayerCount < 4 || randomPlayerCount > clubPlayers.length) {
      alert(`Please select between 4 and ${clubPlayers.length} players`);
      return;
    }

    // Randomly select players
    const shuffled = [...clubPlayers].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, randomPlayerCount).map(p => p.id);
    setSelectedPlayers(selected);

    // Set default target games for all selected players
    const newTargetGames = {};
    selected.forEach(playerId => {
      newTargetGames[playerId] = defaultTargetGames;
    });
    setTargetGames(newTargetGames);
  };

  const handlePlayerToggle = (playerId) => {
    if (selectedPlayers.includes(playerId)) {
      setSelectedPlayers(selectedPlayers.filter(id => id !== playerId));
      const newTargetGames = { ...targetGames };
      delete newTargetGames[playerId];
      setTargetGames(newTargetGames);
    } else {
      setSelectedPlayers([...selectedPlayers, playerId]);
      setTargetGames({
        ...targetGames,
        [playerId]: defaultTargetGames,
      });
    }
  };

  const handleTargetGamesChange = (playerId, value) => {
    const numValue = parseInt(value, 10) || 1;
    setTargetGames({
      ...targetGames,
      [playerId]: numValue,
    });
  };

  const handleAddPartnership = () => {
    // Simple partnership: select two players
    const availablePlayers = clubPlayers.filter(p => selectedPlayers.includes(p.id));
    if (availablePlayers.length < 2) {
      alert('Please select at least 2 players first');
      return;
    }

    // For now, just add a placeholder - in a real implementation, you'd have a UI to select pairs
    alert('Partnership selection UI would go here. For now, partnerships are empty.');
  };

  const handlePresetChange = (presetKey) => {
    setScoringPreset(presetKey);
    const preset = SCORING_PRESETS[presetKey];
    if (preset && preset.weights) {
      setNormalizedWeights(preset.weights);
    }
  };

  const handleRunSimulation = async () => {
    if (selectedPlayers.length < 4) {
      alert('Please select at least 4 players');
      return;
    }

    if (numberOfCourts < 1) {
      alert('Please specify at least 1 court');
      return;
    }

    setIsRunning(true);
    setError(null);

    try {
      // Build playersById map
      const playersById = {};
      clubPlayers.forEach(player => {
        if (selectedPlayers.includes(player.id)) {
          playersById[player.id] = {
            ...player,
            gamesPlayedThisSession: 0,
            effectiveGamesPlayed: 0,
          };
        }
      });

      // Prepare simulation config based on scoring system
      const simulationConfig = {
        selectedPlayers,
        numberOfCourts,
        partnerships,
        targetGames,
        gameTypeRestriction,
        playerCategorization,
        playersById,
        scoringSystem, // Pass which system to use
      };

      // Add appropriate parameters based on scoring system
      if (scoringSystem === 'legacy') {
        simulationConfig.matchmakingParams = matchmakingParams;
      } else {
        // Normalized scoring
        simulationConfig.scoringConfig = {
          weights: normalizedWeights,
        };
      }

      // Variety-aware pool selection flag (applies to both scoring systems)
      simulationConfig.varietyAwarePoolSelection = varietyAwarePoolSelection;
      simulationConfig.scoringFirstMatchmaking = scoringFirstMatchmaking;

      // Run simulation
      const results = await runSessionSimulation(simulationConfig);

      // Transform results to format expected by review components
      const gamesData = transformSimulationToGamesData(results.gameHistory, results.playersById, results.playerCategories);
      const interactionsData = transformSimulationToInteractionsData(results.gameHistory, results.playersById);
      const summaryData = transformSimulationToSummaryData(results.gameHistory, results.playersById, results.playerCategories);

      // Build playerInteractions map for game rating
      const playerInteractionsMap = {};
      const gamesPlayedMap = {};
      
      // Initialize from sessionState
      if (results.sessionState) {
        Object.assign(playerInteractionsMap, results.sessionState.playerInteractions || {});
        Object.assign(gamesPlayedMap, results.sessionState.gamesPlayedThisSession || {});
      }

      setSimulationResults({
        gamesData,
        interactionsData,
        summaryData,
        iterations: results.iterations,
        totalGames: results.gameHistory.length,
        playerInteractions: playerInteractionsMap,
        gamesPlayedThisSession: gamesPlayedMap,
        playerCategories: results.playerCategories, // Full structure with metadata
        playersById: results.playersById,
        startingElos: results.startingElos, // Starting ELOs used for categorization
      });

      // Switch to summary tab after simulation completes
      setActiveTab('summary');
    } catch (err) {
      console.error('Error running simulation:', err);
      setError('Failed to run simulation: ' + err.message);
    } finally {
      setIsRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-app-shell">
        <Card variant="default" size="large" className="text-center py-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <Text className="ml-3">Loading...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error && !club) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-app-shell">
        <Card variant="default" size="large" className="text-center py-12">
          <div className="text-6xl mb-4">🚫</div>
          <Heading level={2} className="mb-4">Error</Heading>
          <Text className="mb-8 text-text-secondary">{error}</Text>
          <Button onClick={() => navigate('/my-clubs')} variant="primary">
            Back to My Clubs
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-app-shell">
      {/* Back Button */}
      <div className="mb-8">
        <BackButton
          onClick={() => {
            navigateBack(navigate, location, clubId, `/club-management/${clubId}`);
          }}
        >
          Back to Management
        </BackButton>
      </div>

      {/* Header */}
      <div className="mb-8">
        <Heading level={1} className="text-display mb-2">Session Simulation</Heading>
        <Text className="text-text-secondary">
          Simulate a full session to evaluate matchmaking algorithm performance
        </Text>
      </div>

      {/* Tabs - Always visible */}
      <div className="border-b border-border-medium">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('settings')}
            className={`
              py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
              ${activeTab === 'settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-medium'
              }
            `}
          >
            Simulation Settings
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            disabled={!simulationResults}
            className={`
              py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
              ${activeTab === 'summary'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-medium'
              }
              ${!simulationResults ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('games')}
            disabled={!simulationResults}
            className={`
              py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
              ${activeTab === 'games'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-medium'
              }
              ${!simulationResults ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Games
          </button>
          <button
            onClick={() => setActiveTab('player-interactions')}
            disabled={!simulationResults}
            className={`
              py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
              ${activeTab === 'player-interactions'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-medium'
              }
              ${!simulationResults ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Player Interactions
          </button>
          <button
            onClick={() => setActiveTab('player-categories')}
            disabled={!simulationResults}
            className={`
              py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
              ${activeTab === 'player-categories'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-medium'
              }
              ${!simulationResults ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Player Categories
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'settings' ? (
        <div className="space-y-6">
          {/* Player Selection - Full Width */}
        <Card variant="default" size="large">
            <div className="p-6">
              <Heading level={3} className="mb-4">Player Selection</Heading>
              
                {/* Random Selection */}
              <div className="mb-4 p-4 bg-surface-subtle rounded-lg border border-border-medium">
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Select Random Players
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="4"
                        max={clubPlayers.length}
                        value={randomPlayerCount}
                        onChange={(e) => setRandomPlayerCount(parseInt(e.target.value, 10) || 4)}
                        className="px-3 py-2 border border-border-medium rounded-md w-32 bg-surface text-text-primary"
                      />
                      <Button
                        onClick={handleSelectRandomPlayers}
                        variant="outline"
                        size="small"
                      >
                        Select Random
                      </Button>
                    </div>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {selectedPlayers.length} player{selectedPlayers.length !== 1 ? 's' : ''} selected
                    </div>
                  </div>
                </div>

                {/* Manual Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                  Or Manually Select Players
                  </label>
                <div className="max-h-64 overflow-y-auto border border-border-medium rounded-lg p-2 bg-surface">
                    {clubPlayers.map(player => (
                      <label
                        key={player.id}
                        className="flex items-center p-2 hover:bg-surface-subtle rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPlayers.includes(player.id)}
                          onChange={() => handlePlayerToggle(player.id)}
                        className="mr-3 w-4 h-4"
                        />
                      <span className={`flex-1 ${getGenderTextColor(player.gender)}`}>
                          {player.first_name} {player.last_name}
                        </span>
                      <span className="text-sm text-text-muted ml-4">
                          Mixed: {player.mixed_elo_rating} | Levels: {player.levels_elo_rating}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
          </Card>

          {/* Simulation Parameters - Grid Layout */}
          <Card variant="default" size="large">
            <div className="p-6">
              <Heading level={3} className="mb-4">Simulation Parameters</Heading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Number of Courts */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Number of Courts
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={numberOfCourts}
                onChange={(e) => setNumberOfCourts(parseInt(e.target.value, 10) || 1)}
                    className="w-full px-3 py-2 border border-border-medium rounded-md bg-surface text-text-primary"
              />
            </div>

                {/* Target Games Default */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                    Target Games Per Player (Default)
              </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={defaultTargetGames}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 1;
                      setDefaultTargetGames(value);
                      // Update all selected players
                      const newTargetGames = {};
                      selectedPlayers.forEach(playerId => {
                        newTargetGames[playerId] = value;
                      });
                      setTargetGames(newTargetGames);
                    }}
                    className="w-full px-3 py-2 border border-border-medium rounded-md bg-surface text-text-primary"
              />
            </div>

                {/* Game Type Restriction */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Game Type Restriction
              </label>
              <select
                value={gameTypeRestriction}
                onChange={(e) => setGameTypeRestriction(e.target.value)}
                    className="w-full px-3 py-2 border border-border-medium rounded-md bg-surface text-text-primary"
              >
                <option value="all_types">All Types</option>
                <option value="balanced_only">Balanced Only</option>
                <option value="mixed_only">Mixed Only</option>
                <option value="levels_only">Levels Only</option>
              </select>
            </div>

            {/* Player Categorization */}
            <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Player Categorization
                  </label>
                  <label className="flex items-center gap-2 h-10">
                <input
                  type="checkbox"
                  checked={playerCategorization}
                  onChange={(e) => setPlayerCategorization(e.target.checked)}
                  className="w-4 h-4"
                />
                    <span className="text-sm text-text-secondary">
                  Enable Player Categorization
                </span>
              </label>
                </div>
            </div>

              {/* Individual Target Games (if players selected) */}
              {selectedPlayers.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border-medium">
                  <label className="block text-sm font-medium text-text-secondary mb-3">
                    Individual Target Games (Optional)
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-border-medium rounded-lg p-3 bg-surface-subtle">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {selectedPlayers.map(playerId => {
                        const player = clubPlayers.find(p => p.id === playerId);
                        return (
                          <div key={playerId} className="flex items-center justify-between p-2 bg-surface rounded border border-border-medium">
                            <span className={`text-sm ${getGenderTextColor(player?.gender)}`}>
                              {player?.first_name} {player?.last_name}
                            </span>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={targetGames[playerId] || defaultTargetGames}
                              onChange={(e) => handleTargetGamesChange(playerId, e.target.value)}
                              className="w-16 px-2 py-1 border border-border-medium rounded-md text-sm bg-surface text-text-primary"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

            {/* Partnerships */}
              <div className="mt-6 pt-6 border-t border-border-medium">
                <label className="block text-sm font-medium text-text-secondary mb-3">
                Partnerships
              </label>
                <div className="flex items-center gap-4">
              <Button
                onClick={handleAddPartnership}
                variant="outline"
                size="small"
              >
                Add Partnership
              </Button>
              {partnerships.length > 0 && (
                    <span className="text-sm text-text-secondary">
                      {partnerships.length} partnership{partnerships.length !== 1 ? 's' : ''} configured
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Matchmaking Parameters */}
          <Card variant="default" size="large">
            <div className="p-6">
              <Heading level={3} className="mb-4">Matchmaking Parameters</Heading>
              
              {/* Scoring System Selector */}
              <div className="mb-6 p-4 bg-info-light dark:bg-primary/10 rounded-lg border border-border-medium">
                <label className="block text-sm font-medium text-text-secondary mb-3">
                  Scoring System
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setScoringSystem('legacy')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      scoringSystem === 'legacy'
                        ? 'border-primary bg-info-light dark:bg-primary/20'
                        : 'border-border-medium bg-surface hover:border-border'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-text-primary">Legacy Scoring</span>
                      {scoringSystem === 'legacy' && (
                        <span className="text-primary">✓</span>
                      )}
                    </div>
                    <p className="text-xs text-text-secondary text-left">
                      Original matchmaking system with penalty-based scoring
                    </p>
                  </button>
                  
                  <button
                    onClick={() => setScoringSystem('normalized')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      scoringSystem === 'normalized'
                        ? 'border-primary bg-info-light dark:bg-primary/20'
                        : 'border-border-medium bg-surface hover:border-border'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-text-primary">Normalized Scoring ✨</span>
                      {scoringSystem === 'normalized' && (
                        <span className="text-primary">✓</span>
                      )}
                    </div>
                    <p className="text-xs text-text-secondary text-left">
                      New system with weighted dimensions (0-100 scale)
                    </p>
                  </button>
                </div>
              </div>

              {/* Legacy Scoring Parameters */}
              {scoringSystem === 'legacy' && (
                <div className="space-y-6">
                  {/* Cooldown */}
                  <div className="flex items-center justify-between p-4 bg-surface-subtle rounded-lg border border-border-medium">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">
                        Enable Cooldown
                      </label>
                      <p className="text-xs text-text-muted">
                        Players must wait after finishing a game before being selected again
                      </p>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={matchmakingParams.enableCooldown}
                        onChange={(e) => setMatchmakingParams(prev => ({ ...prev, enableCooldown: e.target.checked }))}
                        className="w-5 h-5"
                      />
                    </label>
                  </div>

                  {/* Wait Guarantee */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Wait Guarantee Threshold
                    </label>
                    <p className="text-xs text-text-muted mb-2">
                      Number of courts a player must observe before being guaranteed to play
                    </p>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={matchmakingParams.waitGuaranteeThreshold}
                      onChange={(e) => setMatchmakingParams(prev => ({ ...prev, waitGuaranteeThreshold: parseInt(e.target.value, 10) || 4 }))}
                      className="w-full px-3 py-2 border border-border-medium rounded-md bg-surface text-text-primary"
                    />
                  </div>

                  {/* Interaction Penalties */}
                  <div className="space-y-4 p-4 bg-surface-subtle rounded-lg border border-border-medium">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                          Enable Player Interaction Penalties
                        </label>
                        <p className="text-xs text-text-muted">
                          Penalize players who frequently interact with the same opponents/teammates
                        </p>
                      </div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={matchmakingParams.enableInteractionPenalties}
                          onChange={(e) => setMatchmakingParams(prev => ({ ...prev, enableInteractionPenalties: e.target.checked }))}
                          className="w-5 h-5"
                        />
                      </label>
                    </div>

                    {matchmakingParams.enableInteractionPenalties && (
                      <div className="space-y-4 pt-4 border-t border-border-medium">
                        {/* Interaction Penalty Weight */}
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Interaction Penalty Weight: {matchmakingParams.interactionPenaltyWeight.toFixed(1)}
                          </label>
                          <p className="text-xs text-text-muted mb-2">
                            How much interaction penalties affect player selection (0.0 = no effect, 1.0 = full effect)
                          </p>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={matchmakingParams.interactionPenaltyWeight}
                            onChange={(e) => setMatchmakingParams(prev => ({ ...prev, interactionPenaltyWeight: parseFloat(e.target.value) }))}
                            className="w-full accent-primary"
                          />
                          <div className="flex justify-between text-xs text-text-muted mt-1">
                            <span>0.0</span>
                            <span>0.5</span>
                            <span>1.0</span>
                          </div>
                        </div>

                        {/* Same Team Interaction Thresholds */}
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Same Team Interaction Thresholds
                          </label>
                          <p className="text-xs text-text-muted mb-3">
                            Percentage of games where players are on the same team. Low = small penalty, High = heavy penalty (almost blocks).
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-text-secondary mb-1">Low Threshold (%)</label>
                              <p className="text-xs text-text-muted mb-1">Small penalty applies</p>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={matchmakingParams.sameTeamLowThreshold}
                                onChange={(e) => setMatchmakingParams(prev => ({ ...prev, sameTeamLowThreshold: parseInt(e.target.value, 10) || 20 }))}
                                className="w-full px-3 py-2 border border-border-medium rounded-md text-sm bg-surface text-text-primary"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-text-secondary mb-1">High Threshold (%)</label>
                              <p className="text-xs text-text-muted mb-1">Heavy penalty applies</p>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={matchmakingParams.sameTeamHighThreshold}
                                onChange={(e) => setMatchmakingParams(prev => ({ ...prev, sameTeamHighThreshold: parseInt(e.target.value, 10) || 25 }))}
                                className="w-full px-3 py-2 border border-border-medium rounded-md text-sm bg-surface text-text-primary"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Total Interaction Thresholds */}
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-2">
                            Total Interaction Thresholds
                          </label>
                          <p className="text-xs text-text-muted mb-3">
                            Percentage of games where players interact (same team OR opponents). Low = small penalty, High = heavy penalty (almost blocks).
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-text-secondary mb-1">Low Threshold (%)</label>
                              <p className="text-xs text-text-muted mb-1">Small penalty applies</p>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={matchmakingParams.totalLowThreshold}
                                onChange={(e) => setMatchmakingParams(prev => ({ ...prev, totalLowThreshold: parseInt(e.target.value, 10) || 20 }))}
                                className="w-full px-3 py-2 border border-border-medium rounded-md text-sm bg-surface text-text-primary"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-text-secondary mb-1">High Threshold (%)</label>
                              <p className="text-xs text-text-muted mb-1">Heavy penalty applies</p>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={matchmakingParams.totalHighThreshold}
                                onChange={(e) => setMatchmakingParams(prev => ({ ...prev, totalHighThreshold: parseInt(e.target.value, 10) || 50 }))}
                                className="w-full px-3 py-2 border border-border-medium rounded-md text-sm bg-surface text-text-primary"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ELO Balance Multiplier */}
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      ELO Balance Multiplier: {matchmakingParams.eloBalanceMultiplier}
                    </label>
                    <p className="text-xs text-text-muted mb-2">
                      How strictly teams are balanced by ELO (higher = stricter balance)
                    </p>
                    <input
                      type="range"
                      min="10"
                      max="50"
                      step="5"
                      value={matchmakingParams.eloBalanceMultiplier}
                      onChange={(e) => setMatchmakingParams(prev => ({ ...prev, eloBalanceMultiplier: parseInt(e.target.value, 10) || 30 }))}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-xs text-text-muted mt-1">
                      <span>10 (Looser)</span>
                      <span>30 (Default)</span>
                      <span>50 (Stricter)</span>
                    </div>
                  </div>

                  {/* Player Category Gap Hard Block */}
                  {playerCategorization && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Category Gap Hard Block: {matchmakingParams.categoryGapHardBlock}
                      </label>
                      <p className="text-xs text-text-muted mb-2">
                        Block games where players have a category gap at or above this value (e.g., 3 = block games with A-D combinations)
                      </p>
                      <input
                        type="range"
                        min="1"
                        max="4"
                        step="1"
                        value={matchmakingParams.categoryGapHardBlock}
                        onChange={(e) => setMatchmakingParams(prev => ({ ...prev, categoryGapHardBlock: parseInt(e.target.value, 10) || 3 }))}
                        className="w-full accent-primary"
                      />
                      <div className="flex justify-between text-xs text-text-muted mt-1">
                        <span>1 (Only same)</span>
                        <span>2 (Allow 1 gap)</span>
                        <span>3 (Allow 2 gap)</span>
                        <span>4 (Allow all)</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Consecutive Interaction Block */}
                  <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">
                          Block Consecutive Interactions
                        </label>
                        <p className="text-xs text-text-muted">
                          Block players from playing together/against each other for N consecutive games
                        </p>
                      </div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={matchmakingParams.enableConsecutiveInteractionBlock}
                          onChange={(e) => setMatchmakingParams(prev => ({ ...prev, enableConsecutiveInteractionBlock: e.target.checked }))}
                          className="w-5 h-5"
                        />
                      </label>
                    </div>
                    
                    {matchmakingParams.enableConsecutiveInteractionBlock && (
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Consecutive Threshold: {matchmakingParams.consecutiveInteractionThreshold} (Block on {matchmakingParams.consecutiveInteractionThreshold + 1} consecutive)
                        </label>
                        <p className="text-xs text-text-muted mb-2">
                          If two players have interacted for this many consecutive games, block them from playing together in the next game
                        </p>
                        <input
                          type="range"
                          min="1"
                          max="4"
                          step="1"
                          value={matchmakingParams.consecutiveInteractionThreshold}
                          onChange={(e) => setMatchmakingParams(prev => ({ ...prev, consecutiveInteractionThreshold: parseInt(e.target.value, 10) || 2 }))}
                          className="w-full accent-primary"
                        />
                        <div className="flex justify-between text-xs text-text-muted mt-1">
                          <span>1 (Block 2nd)</span>
                          <span>2 (Block 3rd)</span>
                          <span>3 (Block 4th)</span>
                          <span>4 (Block 5th)</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Normalized Scoring Parameters */}
              {scoringSystem === 'normalized' && (
                <div className="space-y-6">
                  {/* Preset Selector */}
                  <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                    <label className="block text-sm font-medium text-text-secondary mb-3">
                      Choose a Preset
                    </label>
                    <p className="text-xs text-text-muted mb-3">
                      Select a preset configuration or customize the weights below
                    </p>
                    <select
                      value={scoringPreset}
                      onChange={(e) => handlePresetChange(e.target.value)}
                      className="w-full px-3 py-2 border border-border-medium rounded-md bg-surface text-text-primary"
                    >
                      <option value="default">Default - Balanced (Fairness 35%, ELO 25%)</option>
                      <option value="competitive">Competitive - ELO First (ELO 45%, Fairness 25%)</option>
                      <option value="social">Social - Fairness First (Fairness 50%, Variety 25%)</option>
                      <option value="variety">Variety - Meet New People (Variety 35%, Fairness 30%)</option>
                      <option value="categoryStrict">Category Strict - Stay in Tier (Categories 20%)</option>
                      <option value="fairnessFirst">Fairness First - Equal Play (Fairness 60%)</option>
                    </select>
                  </div>

                  {/* Scoring-First Matchmaking Toggle */}
                  <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="scoringFirstMatchmaking"
                        checked={scoringFirstMatchmaking}
                        onChange={(e) => setScoringFirstMatchmaking(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-border-medium"
                      />
                      <div>
                        <label htmlFor="scoringFirstMatchmaking" className="text-sm font-medium text-text-primary cursor-pointer">
                          Scoring-First Matchmaking
                        </label>
                        <p className="text-xs text-text-muted mt-1">
                          Eligibility-only pool + full-pool combo enumeration. Fairness, wait pacing, and variety via normalized scoring only (no structural guaranteed/core pools). Supersedes variety-aware pool shaping when enabled.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Variety-Aware Pool Selection Toggle */}
                  <div className="p-4 bg-surface-subtle rounded-lg border border-border-medium">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="varietyAwarePoolSelection"
                        checked={varietyAwarePoolSelection}
                        onChange={(e) => setVarietyAwarePoolSelection(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-border-medium"
                      />
                      <div>
                        <label htmlFor="varietyAwarePoolSelection" className="text-sm font-medium text-text-primary cursor-pointer">
                          Variety-Aware Pool Selection
                        </label>
                        <p className="text-xs text-text-muted mt-1">
                          Shapes the player pool using session saturation score and marginal variety gain before greedy selection. Adds min+1 alternates when the session is becoming repetitive. Debug info shows <code className="text-xs bg-surface px-1 rounded">varietyMetrics</code> per court.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dimension Weights */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-text-secondary">
                        Dimension Weights
                      </label>
                      <span className={`text-sm font-semibold px-2 py-1 rounded ${
                        (Object.values(normalizedWeights).reduce((a, b) => a + b, 0) * 100) > 100
                          ? 'bg-danger-light text-danger-dark'
                          : (Object.values(normalizedWeights).reduce((a, b) => a + b, 0) * 100) < 95
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        Total: {(Object.values(normalizedWeights).reduce((a, b) => a + b, 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="text-xs text-text-muted mb-4">
                      Adjust how much each factor influences matchmaking. All dimensions are scored 0-100, then weighted. Total should equal 100%.
                    </p>

                    {/* Fairness Weight */}
                    <div className="p-3 bg-surface rounded-lg border border-border-medium">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-text-secondary">
                          Fairness (Games Played Balance)
                        </label>
                        <span className="text-sm font-semibold text-primary">
                          {(normalizedWeights.fairness * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="range"
                          min="0"
                          max="1.0"
                          step="0.05"
                          value={normalizedWeights.fairness}
                          onChange={(e) => setNormalizedWeights(prev => ({ ...prev, fairness: parseFloat(e.target.value) }))}
                          className="w-full"
                          style={{
                            background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${normalizedWeights.fairness * 100}%, var(--color-surface-subtle) ${normalizedWeights.fairness * 100}%, var(--color-surface-subtle) 100%)`
                          }}
                        />
                      </div>
                      <p className="text-xs text-text-muted mt-1">
                        Ensures all players get equal game time
                      </p>
                    </div>

                    {/* ELO Balance Weight */}
                    <div className="p-3 bg-surface rounded-lg border border-border-medium">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-text-secondary">
                          ELO Balance (Competitive Balance)
                        </label>
                        <span className="text-sm font-semibold text-primary">
                          {(normalizedWeights.eloBalance * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="range"
                          min="0"
                          max="1.0"
                          step="0.05"
                          value={normalizedWeights.eloBalance}
                          onChange={(e) => setNormalizedWeights(prev => ({ ...prev, eloBalance: parseFloat(e.target.value) }))}
                          className="w-full"
                          style={{
                            background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${normalizedWeights.eloBalance * 100}%, var(--color-surface-subtle) ${normalizedWeights.eloBalance * 100}%, var(--color-surface-subtle) 100%)`
                          }}
                        />
                      </div>
                      <p className="text-xs text-text-muted mt-1">
                        Creates close, balanced matches
                      </p>
                    </div>

                    {/* Interactions Weight */}
                    <div className="p-3 bg-surface rounded-lg border border-border-medium">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-text-secondary">
                          Interactions (Variety/Freshness)
                        </label>
                        <span className="text-sm font-semibold text-primary">
                          {(normalizedWeights.interactions * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="range"
                          min="0"
                          max="1.0"
                          step="0.05"
                          value={normalizedWeights.interactions}
                          onChange={(e) => setNormalizedWeights(prev => ({ ...prev, interactions: parseFloat(e.target.value) }))}
                          className="w-full"
                          style={{
                            background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${normalizedWeights.interactions * 100}%, var(--color-surface-subtle) ${normalizedWeights.interactions * 100}%, var(--color-surface-subtle) 100%)`
                          }}
                        />
                      </div>
                      <p className="text-xs text-text-muted mt-1">
                        Encourages fresh matchups, reduces repeat interactions
                      </p>
                    </div>

                    {/* Game Type Weight */}
                    <div className="p-3 bg-surface rounded-lg border border-border-medium">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-text-secondary">
                          Game Type (Balanced Mix)
                        </label>
                        <span className="text-sm font-semibold text-primary">
                          {(normalizedWeights.gameType * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="relative">
                        <input
                          type="range"
                          min="0"
                          max="1.0"
                          step="0.05"
                          value={normalizedWeights.gameType}
                          onChange={(e) => setNormalizedWeights(prev => ({ ...prev, gameType: parseFloat(e.target.value) }))}
                          className="w-full"
                          style={{
                            background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${normalizedWeights.gameType * 100}%, var(--color-surface-subtle) ${normalizedWeights.gameType * 100}%, var(--color-surface-subtle) 100%)`
                          }}
                        />
                      </div>
                      <p className="text-xs text-text-muted mt-1">
                        Prefers balanced game types (mixed vs mixed, mens vs mens)
                      </p>
                    </div>

                    {/* Categories Weight */}
                    {playerCategorization && (
                      <div className="p-3 bg-surface rounded-lg border border-border-medium">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-text-secondary">
                            Categories (Skill Tier Balance)
                          </label>
                          <span className="text-sm font-semibold text-primary">
                            {(normalizedWeights.categories * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="relative">
                          <input
                            type="range"
                            min="0"
                            max="1.0"
                            step="0.05"
                            value={normalizedWeights.categories}
                            onChange={(e) => setNormalizedWeights(prev => ({ ...prev, categories: parseFloat(e.target.value) }))}
                            className="w-full"
                            style={{
                              background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${normalizedWeights.categories * 100}%, var(--color-surface-subtle) ${normalizedWeights.categories * 100}%, var(--color-surface-subtle) 100%)`
                            }}
                          />
                        </div>
                        <p className="text-xs text-text-muted mt-1">
                          Keeps players within similar skill tiers
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-info-light dark:bg-primary/10 rounded-lg border border-border-medium">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">ℹ️</span>
                      <div className="text-xs text-text-secondary">
                        <p className="font-medium text-text-secondary mb-1">How Normalized Scoring Works:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Each dimension scores 0-100 (100 = perfect)</li>
                          <li>Scores are multiplied by their weights</li>
                          <li>Final score = sum of weighted dimension scores</li>
                          <li>Higher final score = better match</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

            {/* Run Simulation Button */}
          <Card variant="default" size="large">
            <div className="p-6">
              <Button
                onClick={handleRunSimulation}
                variant="primary"
                size="large"
                disabled={isRunning || selectedPlayers.length < 4}
                className="w-full"
              >
                {isRunning ? 'Running Simulation...' : 'Run Simulation'}
              </Button>
              {error && (
                <div className="mt-4 p-3 bg-danger-light border border-danger-dark/30 rounded text-danger-dark text-sm">
                  {error}
                </div>
              )}
              {selectedPlayers.length < 4 && (
                <div className="mt-2 text-sm text-text-muted text-center">
                  Please select at least 4 players to run a simulation
            </div>
              )}
          </div>
        </Card>
        </div>
      ) : activeTab === 'summary' && simulationResults ? (
          <Card variant="default" size="large">
            <div className="p-6">
            <Heading level={3} className="mb-4">Simulation Summary</Heading>
            <SimulationSummary
              gamesData={simulationResults.gamesData}
              playerInteractions={simulationResults.playerInteractions}
              gamesPlayedThisSession={simulationResults.gamesPlayedThisSession}
              summaryData={simulationResults.summaryData}
            />
                  </div>
        </Card>
      ) : activeTab === 'summary' && !simulationResults ? (
        <Card variant="default" size="large">
          <div className="p-6">
            <div className="text-center py-12">
              <p className="text-text-muted">Run a simulation to see the summary results.</p>
              </div>
            </div>
          </Card>

      ) : activeTab === 'games' && simulationResults ? (
        <div className="space-y-6">
          {/* Player Filter */}
            <Card variant="default" size="medium">
              <div className="p-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-text-secondary whitespace-nowrap">
                    Filter by Player:
                  </label>
                  <div className="flex-1 max-w-md">
                    <PlayerSearchInput
                      placeholder="Search players to filter..."
                      availablePlayers={selectedPlayers.map(playerId => {
                        const player = clubPlayers.find(p => p.id === playerId);
                        return player ? {
                          id: player.id,
                          first_name: player.first_name,
                          last_name: player.last_name,
                          gender: player.gender,
                        } : null;
                      }).filter(Boolean)}
                      selectedPlayer={selectedFilterPlayer}
                      onSelectPlayer={(player) => setSelectedFilterPlayer(player)}
                      onRemovePlayer={() => setSelectedFilterPlayer(null)}
                      showSelectedInfo={true}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </Card>

          {/* Games Table */}
            <Card variant="default" size="large">
              <div className="p-6">
                <Heading level={3} className="mb-4">Games Table</Heading>
                <SessionReviewGamesTable 
                  simulationData={simulationResults.gamesData}
                  filterPlayerIds={selectedFilterPlayer ? [selectedFilterPlayer.id] : []}
                  playerInteractions={simulationResults.playerInteractions}
                  gamesPlayedThisSession={simulationResults.gamesPlayedThisSession}
                  hideGameLinks={true}
                />
              </div>
            </Card>
        </div>
      ) : activeTab === 'games' && !simulationResults ? (
        <Card variant="default" size="large">
          <div className="p-6">
            <div className="text-center py-12">
              <p className="text-text-muted">Run a simulation to see the games table.</p>
            </div>
          </div>
        </Card>
      ) : activeTab === 'player-interactions' && simulationResults ? (
            <div className="space-y-6">
              <Card variant="default" size="large">
                <div className="p-6">
                  <Heading level={3} className="mb-4">Player Interaction Summary</Heading>
                  <SessionReviewPlayerSummaryTable 
                    simulationData={simulationResults.summaryData}
                    filterPlayerIds={selectedFilterPlayer ? [selectedFilterPlayer.id] : []}
                  />
                </div>
              </Card>
              <Card variant="default" size="large">
                <div className="p-6">
                  <Heading level={3} className="mb-4">Player Interactions</Heading>
                  <SessionReviewInteractionsTable 
                    simulationData={simulationResults.interactionsData}
                    filterPlayerIds={selectedFilterPlayer ? [selectedFilterPlayer.id] : []}
                  />
                </div>
              </Card>
            </div>
          ) : activeTab === 'player-interactions' && !simulationResults ? (
        <Card variant="default" size="large">
          <div className="p-6">
            <div className="text-center py-12">
              <p className="text-text-muted">Run a simulation to see player interactions.</p>
            </div>
          </div>
        </Card>
      ) : activeTab === 'player-categories' && simulationResults ? (
            <Card variant="default" size="large">
              <div className="p-6">
                <Heading level={3} className="mb-4">Player Categories</Heading>
                {simulationResults.playerCategories && simulationResults.playersById ? (
                  <PlayerCategoriesTable
                    players={Object.values(simulationResults.playersById)}
                    playerCategories={simulationResults.playerCategories}
                    startingElos={simulationResults.startingElos}
                    showMixed={true}
                    showLevels={true}
                  />
                ) : (
                  <div className="p-4 border border-dashed border-border-medium rounded-lg text-sm text-text-secondary">
                    Player categorization was not enabled for this simulation.
        </div>
      )}
              </div>
            </Card>
          ) : activeTab === 'player-categories' && !simulationResults ? (
        <Card variant="default" size="large">
          <div className="p-6">
            <div className="text-center py-12">
              <p className="text-text-muted">Run a simulation to see player categories.</p>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
};

export default SessionSimulation;

